/* design-picker.js
 * Three step buttons (Inside the cake, Flavour, Size) for the "design" product template.
 * It never computes price, photo, cart or address: it sets the theme's own native
 * dropdowns and fires one bubbling `change`, so theme.js does the rest.
 * Pure selection logic is exported (node) or attached to window.DesignPickerLogic.
 */
(function () {
  'use strict';

  var G = typeof window !== 'undefined' ? window : globalThis;

  var OPTION_NAMES = ['Inside the cake', 'Flavour', 'Size'];
  var INSIDE_ORDER = ['Sponge Cake', 'Crepe Cake', 'Basque Cheesecake'];
  var SIZE_ORDER = ['Tea', '4 inch', '6 inch', '9 inch', 'Slab 16 x 11 inch'];
  var SERVES = {
    'Tea': 'serves 2 to 4',
    '4 inch': 'serves 3 to 4',
    '6 inch': 'serves 4 to 8',
    '9 inch': 'serves 8 to 14',
    'Slab 16 x 11 inch': 'serves 40'
  };
  var HIDE_FLAVOUR_WHEN_ONLY = 'Classic';

  /* ---------- pure logic: variants are {id,o1,o2,o3,price(cents),available} ---------- */

  function uniq(list) {
    var out = [];
    list.forEach(function (x) { if (out.indexOf(x) === -1) out.push(x); });
    return out;
  }
  function rank(order, value) {
    var i = order.indexOf(value);
    return i === -1 ? order.length : i;
  }
  function byOrder(order) {
    return function (a, b) { return rank(order, a) - rank(order, b); };
  }
  function insides(variants) {
    var seen = uniq(variants.map(function (v) { return v.o1; }));
    return seen.map(function (x, i) { return [x, i]; })
      .sort(function (a, b) { return (rank(INSIDE_ORDER, a[0]) - rank(INSIDE_ORDER, b[0])) || (a[1] - b[1]); })
      .map(function (p) { return p[0]; });
  }
  function flavours(variants, inside) {
    return uniq(variants.filter(function (v) { return v.o1 === inside; }).map(function (v) { return v.o2; }));
  }
  function sizes(variants, inside, flavour) {
    var list = uniq(variants.filter(function (v) { return v.o1 === inside && v.o2 === flavour; })
      .map(function (v) { return v.o3; }));
    return list.map(function (x, i) { return [x, i]; })
      .sort(function (a, b) { return (rank(SIZE_ORDER, a[0]) - rank(SIZE_ORDER, b[0])) || (a[1] - b[1]); })
      .map(function (p) { return p[0]; });
  }
  function find(variants, inside, flavour, size) {
    var hits = variants.filter(function (v) { return v.o1 === inside && v.o2 === flavour && v.o3 === size; });
    return hits.length === 1 ? hits[0] : null;
  }
  /* cheapest available of a list; if none available, cheapest overall; ties keep list order */
  function cheapest(list) {
    if (!list.length) return null;
    var avail = list.filter(function (v) { return v.available; });
    var pool = avail.length ? avail : list;
    return pool.reduce(function (best, v) { return v.price < best.price ? v : best; }, pool[0]);
  }
  function anyAvailable(list) {
    return list.some(function (v) { return v.available; });
  }
  /* sold-out flags for the buttons of one step */
  function insideSoldOut(variants, inside) {
    return !anyAvailable(variants.filter(function (v) { return v.o1 === inside; }));
  }
  function flavourSoldOut(variants, inside, flavour) {
    return !anyAvailable(variants.filter(function (v) { return v.o1 === inside && v.o2 === flavour; }));
  }
  function sizeSoldOut(variants, inside, flavour, size) {
    var v = find(variants, inside, flavour, size);
    return !v || !v.available;
  }
  /* sel = current variant; change = {step:'inside'|'flavour'|'size', value}. Returns the new variant or null. */
  function choose(variants, sel, change) {
    var inside = sel.o1, flavour = sel.o2, size = sel.o3, pool, hit;
    if (change.step === 'size') {
      return find(variants, inside, flavour, change.value);
    }
    if (change.step === 'flavour') {
      hit = find(variants, inside, change.value, size);
      if (hit) return hit;
      return cheapest(variants.filter(function (v) { return v.o1 === inside && v.o2 === change.value; }));
    }
    if (change.step === 'inside') {
      hit = find(variants, change.value, flavour, size);
      if (hit) return hit;
      pool = variants.filter(function (v) { return v.o1 === change.value && v.o2 === flavour; });
      if (pool.length) return cheapest(pool);
      return cheapest(variants.filter(function (v) { return v.o1 === change.value; }));
    }
    return null;
  }
  function money(cents) {
    var d = cents / 100;
    return '$' + (cents % 100 === 0 ? String(d) : d.toFixed(2));
  }
  function summary(v) {
    var parts = [v.o1];
    if (v.o2 !== HIDE_FLAVOUR_WHEN_ONLY) parts.push(v.o2);
    parts.push(v.o3);
    return parts.join(', ') + ': ' + money(v.price);
  }
  /* the sentence that goes with an inside (e.g. the Basque height note), or '' */
  function noteFor(notes, inside) {
    var t = notes && typeof notes === 'object' ? notes[inside] : null;
    return typeof t === 'string' ? t.trim() : '';
  }
  function showFlavourStep(variants, inside) {
    var f = flavours(variants, inside);
    return !(f.length === 1 && f[0] === HIDE_FLAVOUR_WHEN_ONLY);
  }
  /* starting variant: ?variant= if it is one of ours, else the first variant (cheapest) */
  function initial(variants, variantIdFromAddress) {
    var id = String(variantIdFromAddress || '');
    var hit = variants.filter(function (v) { return String(v.id) === id; })[0];
    return hit || variants[0] || null;
  }
  function fromNative(variants, o1, o2, o3) {
    return find(variants, o1, o2, o3);
  }

  var Logic = {
    OPTION_NAMES: OPTION_NAMES, SIZE_ORDER: SIZE_ORDER, SERVES: SERVES,
    insides: insides, flavours: flavours, sizes: sizes, find: find, cheapest: cheapest,
    choose: choose, money: money, summary: summary, showFlavourStep: showFlavourStep,
    initial: initial, fromNative: fromNative, noteFor: noteFor,
    insideSoldOut: insideSoldOut, flavourSoldOut: flavourSoldOut, sizeSoldOut: sizeSoldOut
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = Logic;
  } else {
    G.DesignPickerLogic = Logic;
  }

  /* ---------- browser part ---------- */
  if (typeof document === 'undefined') return;

  function el(tag, attrs, text) {
    var n = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) { n.setAttribute(k, attrs[k]); });
    if (text != null) n.textContent = text;
    return n;
  }

  function init() {
    var root = document.querySelector('[data-design-picker]');
    if (!root) return;
    var form = null;
    function bail() {
      try { if (form) form.classList.remove('dpk-active'); } catch (e) { /* nothing */ }
      while (root.firstChild) root.removeChild(root.firstChild);
      root.hidden = true;
    }
    try {
      var data = JSON.parse(document.getElementById('dpk-data').textContent);
      var names = data.options;
      if (!names || names.join('|') !== OPTION_NAMES.join('|')) throw new Error('option names differ');
      var variants = data.variants.map(function (v) {
        return { id: v.id, o1: v.o1, o2: v.o2, o3: v.o3, price: v.price, available: !!v.available, img: v.img, src: v.src };
      });
      if (!variants.length) throw new Error('no variants');
      var notes = data.notes || null;
      var ref = data.ref && data.ref.src ? data.ref : null;

      /* the theme's own selects: product.liquid renders select.single-option-selector[data-index=optionN] inside form[data-product-form] */
      form = document.querySelector('form[data-product-form]');
      if (!form) throw new Error('product form not found');
      var natives = [1, 2, 3].map(function (n) {
        var s = form.querySelector('select.single-option-selector[data-index="option' + n + '"]');
        if (!s) throw new Error('native select option' + n + ' not found');
        return s;
      });
      var anchor = natives[0].closest('.product-form__controls-group');
      if (!anchor) throw new Error('controls group not found');

      var current = fromNative(variants, natives[0].value, natives[1].value, natives[2].value) ||
        initial(variants, new URLSearchParams(location.search).get('variant'));
      if (!current) throw new Error('no starting variant');

      var drawing = false;

      function stepBox(title) {
        var box = el('div', { 'class': 'dpk-step', role: 'group' });
        var h = el('div', { 'class': 'dpk-step__title', id: 'dpk-t-' + title.replace(/\W/g, '') }, title);
        box.setAttribute('aria-labelledby', h.id);
        box.appendChild(h);
        var row = el('div', { 'class': 'dpk-row' });
        box.appendChild(row);
        return { box: box, row: row };
      }
      function button(parts, pressed, soldOut, onPick) {
        var b = el('button', { type: 'button', 'class': 'dpk-btn', 'aria-pressed': pressed ? 'true' : 'false' });
        parts.forEach(function (p) { b.appendChild(el('span', { 'class': 'dpk-btn__' + p[0] }, p[1])); });
        if (soldOut) {
          b.disabled = true;
          b.appendChild(el('span', { 'class': 'dpk-btn__sold' }, 'Sold out'));
        } else {
          b.addEventListener('click', onPick);
        }
        return b;
      }
      function pick(step, value) {
        var next = choose(variants, current, { step: step, value: value });
        if (!next) return;
        /* a throw here must not leave the customer with hidden dropdowns and dead buttons */
        try { apply(next); } catch (e) { bail(); if (G.console && console.warn) console.warn('design-picker: native dropdowns kept', e); }
      }
      function build(sel) {
        var frag = document.createDocumentFragment();
        var s1 = stepBox('Inside the cake');
        insides(variants).forEach(function (i) {
          s1.row.appendChild(button([['label', i]], i === sel.o1, insideSoldOut(variants, i), function () { pick('inside', i); }));
        });
        var note = noteFor(notes, sel.o1);
        if (note) {
          s1.box.appendChild(el('p', { 'class': 'dpk-note' }, note));
          if (ref) {
            var link = el('a', { 'class': 'dpk-note__ref', href: ref.full || ref.src, target: '_blank', rel: 'noopener' });
            link.appendChild(el('img', { src: ref.src, alt: ref.alt || '', loading: 'lazy' }));
            s1.box.appendChild(link);
          }
        }
        frag.appendChild(s1.box);
        if (showFlavourStep(variants, sel.o1)) {
          var s2 = stepBox('Flavour');
          flavours(variants, sel.o1).forEach(function (f) {
            s2.row.appendChild(button([['label', f]], f === sel.o2, flavourSoldOut(variants, sel.o1, f), function () { pick('flavour', f); }));
          });
          frag.appendChild(s2.box);
        }
        var s3 = stepBox('Size');
        sizes(variants, sel.o1, sel.o2).forEach(function (z) {
          var v = find(variants, sel.o1, sel.o2, z);
          s3.row.appendChild(button(
            [['label', z], ['serves', SERVES[z] || ''], ['price', money(v.price)]],
            z === sel.o3, !v.available, function () { pick('size', z); }));
        });
        frag.appendChild(s3.box);
        var sum = el('p', { 'class': 'dpk-summary', 'aria-live': 'polite' }, summary(sel));
        frag.appendChild(sum);
        return frag;
      }
      function draw(sel) {
        var frag = build(sel); /* built off-page first: a throw here leaves the page untouched */
        while (root.firstChild) root.removeChild(root.firstChild);
        root.appendChild(frag);
      }
      /* set all three native selects, then ONE change event: theme.js reads all three on change */
      function apply(v) {
        current = v;
        [v.o1, v.o2, v.o3].forEach(function (val, i) {
          var ok = Array.prototype.some.call(natives[i].options, function (o) { return o.value === val; });
          if (!ok) throw new Error('native option missing: ' + val);
          natives[i].value = val;
        });
        drawing = true;
        try { natives[2].dispatchEvent(new Event('change', { bubbles: true })); } finally { drawing = false; }
        draw(v);
      }

      /* first paint: place the root above the dropdowns' group, draw, only then hide natives */
      anchor.parentNode.insertBefore(root, anchor);
      draw(current);
      /* the buttons exist; if the page's own value differs from ours (e.g. ?variant=), make the theme agree */
      var nat = fromNative(variants, natives[0].value, natives[1].value, natives[2].value);
      if (!nat || nat.id !== current.id) apply(current);
      root.hidden = false;
      form.classList.add('dpk-active');

      /* the theme or a thumbnail click may change a native select: follow it */
      natives.forEach(function (s) {
        s.addEventListener('change', function () {
          if (drawing) return;
          try {
            var v = fromNative(variants, natives[0].value, natives[1].value, natives[2].value);
            if (v) { current = v; draw(v); }
          } catch (e) { bail(); }
        });
      });
    } catch (err) {
      bail();
      if (G.console && console.warn) console.warn('design-picker: native dropdowns kept', err);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
